import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Columns3, Loader2 } from 'lucide-react';
import { SACell } from './SACell';
import { API, authHeaders } from './api';

export const ServerTable = ({ endpoint, params, columns, testId, defaultSort = '', itemKey = 'row_id' }) => {
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const [sort, setSort] = useState({ by: defaultSort, dir: 'desc' });
  const [hidden, setHidden] = useState(() => new Set(columns.filter((c) => c.hidden).map((c) => c.key)));
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  useEffect(() => { setPage(1); }, [params]);
  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    axios.get(`${API}${endpoint}`, { headers: authHeaders(), signal: ctrl.signal,
      params: { ...params, page, page_size: size, sort_by: sort.by, sort_dir: sort.dir } })
      .then((r) => { setData(r.data); setLoading(false); })
      .catch((e) => { if (e.name !== 'CanceledError') setLoading(false); });
    return () => ctrl.abort();
  }, [endpoint, params, page, size, sort]);

  const cols = columns.filter((c) => !hidden.has(c.key));
  const pages = Math.max(1, Math.ceil(data.total / size));
  const toggleCol = (k) => setHidden((h) => { const n = new Set(h); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  return (
    <div className="rounded-xl border border-orange-200/70 bg-white overflow-hidden" data-testid={testId}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-orange-100 text-xs text-stone-600">
        <span data-testid={`${testId}-count`}>{data.total.toLocaleString('en-IN')} records {loading && <Loader2 size={12} className="inline animate-spin ml-1" />}</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" data-testid={`${testId}-columns-btn`}><Columns3 size={13} />Columns</Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 max-h-80 overflow-auto p-2" align="end">
            {columns.map((c) => (
              <label key={c.key} className="flex items-center gap-2 text-xs py-1 px-1 hover:bg-orange-50 rounded cursor-pointer">
                <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggleCol(c.key)} data-testid={`${testId}-col-${c.key}`} />{c.label}
              </label>
            ))}
          </PopoverContent>
        </Popover>
      </div>
      <div className="overflow-auto max-h-[64vh]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-[#3b2416] text-orange-50">
            <tr>
              <th className="w-8" />
              {cols.map((c) => (
                <th key={c.key} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                  <button type="button" className="flex items-center gap-1 hover:text-white" data-testid={`${testId}-sort-${c.key}`}
                    onClick={() => setSort((s) => ({ by: c.key, dir: s.by === c.key && s.dir === 'desc' ? 'asc' : 'desc' }))}>
                    {c.label}<ArrowUpDown size={11} className={sort.by === c.key ? 'opacity-100 text-orange-300' : 'opacity-40'} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!data.items.length && !loading && (
              <tr><td colSpan={cols.length + 1} className="px-3 py-10 text-center text-stone-500" data-testid={`${testId}-empty`}>No records match the selected filters</td></tr>
            )}
            {data.items.map((r, i) => {
              const id = r[itemKey] || i;
              return (
                <React.Fragment key={id}>
                  <tr className="border-t border-orange-50 hover:bg-orange-50/60 transition-colors" data-testid={`${testId}-row`}>
                    <td className="px-2">
                      <button type="button" onClick={() => setOpen(open === id ? null : id)} className="text-stone-400 hover:text-orange-700" data-testid={`${testId}-expand`}>
                        {open === id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                    {cols.map((c) => (
                      <td key={c.key} className={`px-3 py-2 whitespace-nowrap text-stone-700 ${['money', 'qty', 'pct'].includes(c.type) ? 'text-right tabular-nums' : ''} ${c.wide ? 'max-w-xs truncate' : ''}`}
                        title={typeof r[c.key] === 'string' ? r[c.key] : undefined}>
                        <SACell col={c} value={r[c.key]} row={r} />
                      </td>
                    ))}
                  </tr>
                  {open === id && (
                    <tr className="bg-orange-50/40" data-testid={`${testId}-expanded`}>
                      <td colSpan={cols.length + 1} className="px-6 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-x-6 gap-y-2">
                          {columns.map((c) => (
                            <div key={c.key} className="text-[11px]"><div className="text-stone-500">{c.label}</div>
                              <div className="font-medium text-stone-800 break-words"><SACell col={c} value={r[c.key]} row={r} /></div></div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-2 border-t border-orange-100 text-xs text-stone-600">
        <select value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }} className="border rounded px-2 py-1" data-testid={`${testId}-page-size`}>
          {[25, 50, 100, 250].map((n) => <option key={n} value={n}>{n} / page</option>)}
        </select>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}><ChevronLeft size={14} /></Button>
          <span>Page {page} / {pages}</span>
          <Button size="sm" variant="ghost" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}><ChevronRight size={14} /></Button>
        </div>
      </div>
    </div>
  );
};
